import { FileText } from "lucide-react";

import type { ChunkResult } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { highlightQuery } from "@/lib/highlight";

interface Props {
  hit: ChunkResult;
  query: string;
}

function snippetOf(text: string, query: string, max = 300): string {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return text.slice(0, max);
  const idx = text.toLowerCase().indexOf(trimmed);
  if (idx < 0) return text.slice(0, max);
  const start = Math.max(0, idx - 80);
  const end = Math.min(text.length, idx + max - 80);
  const snippet = text.slice(start, end);
  return (start > 0 ? "..." : "") + snippet + (end < text.length ? "..." : "");
}

export function ChunkCard({ hit, query }: Props) {
  const snippet = snippetOf(hit.content, query);
  const meta = hit.metadata ?? {};
  const page = (meta as { page?: number }).page;
  const sheet = (meta as { sheet?: string }).sheet;
  const scorePct = Math.round(hit.score * 100);

  return (
    <article className="space-y-2 rounded-md border bg-card p-3 shadow-sm">
      <header className="flex items-center gap-2 text-sm font-medium">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="truncate">{hit.document_filename}</span>
      </header>
      <p className="text-sm leading-relaxed">
        {highlightQuery(snippet, query)}
      </p>
      <footer className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {page !== undefined ? (
          <Badge variant="outline">Pagina {page}</Badge>
        ) : null}
        {sheet ? <Badge variant="outline">Sheet {sheet}</Badge> : null}
        <div className="ml-auto flex items-center gap-2">
          <span>Score</span>
          <Progress value={scorePct} className="h-1 w-24" />
          <span>{scorePct}%</span>
        </div>
      </footer>
    </article>
  );
}
